import React, { useState, useEffect, useCallback, useRef } from "react";
import I18n from "i18n.js.erb";
import ExpoTotemTile, { expoTotemStateLabel } from "./ExpoTotemTile";
import LoopStatsChart from "./ExpoTotemStats";
import ExpoTotemGrid from "./ExpoTotemGrid";
import ExpoTotemAccordionStats from "./ExpoTotemAccordionStats";
import ErrorModal from "./ErrorModal";
import LoopCellEditModal from "./LoopCellEditModal";
import { debounce, MEDIUM_DEBOUNCE_TIME, parseShortAddress } from "../../utils/utils";
import consumer from "../../channels/consumer";

const LoopDashboard = ({ totems, userId }) => {
  // I totem arrivano dal server al caricamento pagina e poi via websocket a ogni lettura del totem
  const [dashboard, setDashboard] = useState(totems);
  const globalStats = dashboard.global_stats || [];
  const totemsList = dashboard.totems || [];
  const isSingleTotem = totemsList.length === 1;
  const showTotemFilters = !isSingleTotem;
  // Le mensole si aprono indipendentemente per ogni espositore, la dashboard sopra resta sempre visibile.
  // Con piu' espositori si parte tutto chiuso, con uno solo le mensole sono sempre aperte
  const [openTotemIds, setOpenTotemIds] = useState(isSingleTotem ? [totemsList[0].id] : []);
  const [showDetails, setShowDetails] = useState(false);
  const toggleDetails = useCallback(() => setShowDetails(prev => !prev), []);
  const initialFilters = { name: '', serial_number: '', price_from: '', price_to: '', grade_from: '', grade_to: '' };
  const initialTotemFilters = { address: '', store_name: '', totem_serial: '', state: '' };
  const [filters, setFilters] = useState(initialFilters);
  const [totemFilters, setTotemFilters] = useState(initialTotemFilters);
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const [totemCellsMap, setTotemCellsMap] = useState({});
  const [loadingTotemIds, setLoadingTotemIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCellModal, setShowCellModal] = useState(false);
  const [cellToEdit, setCellToEdit] = useState(null);
  const handleTotemSelect = useCallback((totemId) => {
    if (isSingleTotem) return;
    setOpenTotemIds(prev => prev.includes(totemId) ? prev.filter(id => id !== totemId) : [...prev, totemId]);
  }, [isSingleTotem]);

  // Celle caricate solo alla prima apertura di ogni espositore, poi restano in cache
  useEffect(() => {
    const toFetch = openTotemIds.filter(id => !totemCellsMap[id] && !loadingTotemIds.includes(id));
    if (toFetch.length === 0) return;

    setLoadingTotemIds(prev => [...prev, ...toFetch]);

    toFetch.forEach((totemId) => {
      fetch(`/users/${userId}/expo_totems/${totemId}.json`, {
        method: "GET",
      })
        .then((response) => response.json())
        .then((data) => {
          setTotemCellsMap(prev => ({ ...prev, [totemId]: data }));
        })
        .catch((error) => {
          console.error("ERROR:", error);
          setShowErrorModal(true);
        })
        .finally(() => setLoadingTotemIds(prev => prev.filter(id => id !== totemId)));
    });
  }, [openTotemIds]);

  const [searchResults, setSearchResults] = useState({});
  const [hasActiveSearch, setHasActiveSearch] = useState(false);


  const fetchSearchResults = useCallback((currentFilters) => {
    const params = new URLSearchParams();
    Object.entries(currentFilters).forEach(([k, v]) => { if (v !== '') params.append(k, v); });

    fetch(`/users/${userId}/expo_totems.json?${params.toString()}`, {
      method: "GET",
    })
      .then((response) => response.json())
      .then((data) => {
        setSearchResults(data);
        setHasActiveSearch(true);
      })
      .catch((error) => {
        console.error("ERROR:", error);
        setShowErrorModal(true);
      });
  }, [userId]);

  const onChangeFilterHandler = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const onChangeTotemFilterHandler = (key, value) => {
    setTotemFilters(prev => ({ ...prev, [key]: value }));
  };

  const debouncedFetchResults = useCallback(
    debounce(() => fetchSearchResults(filters), MEDIUM_DEBOUNCE_TIME),
    [filters.name, filters.serial_number, filters.price_from, filters.price_to, filters.grade_from, filters.grade_to]
  );

  useEffect(() => {
    const hasFilters = Object.values(filters).some(v => v !== '');
    if (hasFilters) {
      debouncedFetchResults();
    } else {
      setSearchResults({});
      setHasActiveSearch(false);
    }
  }, [filters]);

  const handleLabelClick = useCallback((cell) => {
    setCellToEdit(cell);
    setShowCellModal(true);
  }, []);

  const handleCloseCellModal = useCallback(() => {
    setShowCellModal(false);
    setCellToEdit(null);
  }, []);

  // Con piu' espositori aperti la cella modificata si cerca in tutti quelli gia' caricati
  const handleCellUpdateSuccess = useCallback((cell, newPrice, newLines) => {
    setTotemCellsMap(prev => Object.fromEntries(Object.entries(prev).map(([totemId, totemData]) => {
      if (!totemData || !totemData.cells) return [totemId, totemData];
      return [totemId, {
        ...totemData,
        cells: totemData.cells.map(c => {
          if (c.product_id !== cell.product_id) return c;
          const updatedLabel = { ...c.label };
          if (newPrice != null) updatedLabel.price = newPrice;
          if (newLines) Object.assign(updatedLabel, newLines);
          return { ...c, label: updatedLabel };
        })
      }];
    })));
  }, []);

  // Aggiornamento in diretta: il totem notifica la lettura al server, che spinge qui tile, celle e
  // statistiche gia' pronte. Gli update usano solo la forma funzionale, cosi' l'handler resta stabile
  // e la subscription non va ricreata a ogni render
  const handleExpoTotemUpdate = useCallback((data) => {
    setDashboard(prev => ({
      ...prev,
      global_stats: data.global_stats || prev.global_stats,
      // La tile arriva intera dallo stesso partial del caricamento pagina, quindi si sostituisce invece
      // di fondersi: una fusione non riuscirebbe a togliere un indirizzo che il server non manda piu'
      totems: (prev.totems || []).map(totem => totem.id === data.totem_id && data.totem ? data.totem : totem)
    }));

    if (data.details) {
      setTotemCellsMap(prev => ({ ...prev, [data.totem_id]: data.details }));
    }
  }, []);

  // Dopo una riconnessione i messaggi persi non vengono ripetuti, quindi la griglia resterebbe ferma
  // a prima del buco: si riallinea rileggendo lo stato dal server. La cache dei totem chiusi viene
  // buttata, cosi' riaprirli li ricarica invece di mostrare quello che era vero prima
  const openTotemIdsRef = useRef(openTotemIds);
  useEffect(() => { openTotemIdsRef.current = openTotemIds; }, [openTotemIds]);

  const resyncDashboard = useCallback(() => {
    const totemIds = openTotemIdsRef.current;
    setTotemCellsMap(prev => Object.fromEntries(totemIds.filter(id => prev[id]).map(id => [id, prev[id]])));

    fetch(`/users/${userId}/expo_totems.json`, { method: "GET" })
      .then((response) => response.json())
      .then((data) => { if (data && data.totems) setDashboard(data); })
      .catch((error) => console.warn('Loop, riallineamento totem non riuscito:', error));

    totemIds.forEach((totemId) => {
      fetch(`/users/${userId}/expo_totems/${totemId}.json`, { method: "GET" })
        .then((response) => response.json())
        .then((data) => { if (data && data.cells) setTotemCellsMap(prev => ({ ...prev, [totemId]: data })); })
        .catch((error) => console.warn('Loop, riallineamento celle non riuscito:', error));
    });
  }, [userId]);

  // Il tempo reale e' un di piu': se il websocket non parte o viene rifiutato la dashboard resta
  // quella di prima, con i dati del caricamento pagina e il refresh manuale. Niente modale di errore
  // e niente eccezioni che farebbero smontare la pagina, perche' la sottoscrizione apre il socket
  // in modo sincrono e certi browser la bloccano
  useEffect(() => {
    let subscription = null;
    let hasConnected = false;

    try {
      subscription = consumer.subscriptions.create(
        { channel: "ExpoTotemsChannel", user_id: userId },
        {
          // Al primo aggancio i dati sono quelli appena renderizzati: c'e' da riallineare solo
          // quando si torna su dopo una caduta
          connected() {
            try {
              if (hasConnected) resyncDashboard();
            } catch (error) {
              console.warn('Loop, riallineamento non riuscito:', error);
            }
            hasConnected = true;
          },
          received(data) {
            try {
              if (data.type === 'expo_totem_updated') handleExpoTotemUpdate(data);
            } catch (error) {
              console.warn('Loop live update ignorato:', error);
            }
          }
        }
      );
    } catch (error) {
      console.warn('Loop live update non disponibile:', error);
    }

    const handleUnsubscribe = () => {
      if (!subscription) return;
      try {
        consumer.subscriptions.remove(subscription);
      } catch (error) {
        console.warn('Loop live update, disconnessione fallita:', error);
      }
      subscription = null;
    };
    document.addEventListener('turbolinks:before-cache', handleUnsubscribe);

    return () => {
      handleUnsubscribe();
      document.removeEventListener('turbolinks:before-cache', handleUnsubscribe);
    };
  }, [userId, handleExpoTotemUpdate, resyncDashboard]);

  const getHighlightedCells = useCallback((totemId) => {
    if (!hasActiveSearch || !searchResults[totemId]) return new Set();
    return new Set(searchResults[totemId].map(c => `${c.row}-${c.col}`));
  }, [hasActiveSearch, searchResults]);


  const addressOptions = Array.from(new Set(
    totemsList
      .map((totem) => totem.shipment_address ? parseShortAddress(totem.shipment_address) : '')
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  const storeOptions = Array.from(new Set(
    totemsList
      .map((totem) => totem.store_name || '')
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  const stateOptions = Array.from(new Set(
    totemsList
      .map((totem) => totem.state || '')
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));
  

  const filteredTotemsList = totemsList.filter((totem) => {
    const shortAddress = totem.shipment_address ? parseShortAddress(totem.shipment_address) : '';
    const normalizedTotemSerial = (totem.serial_number || '').toLowerCase();
    const normalizedStoreName = (totem.store_name || '').toLowerCase();
    const normalizedAddress = shortAddress.toLowerCase();
    const normalizedState = (totem.state || '').toLowerCase();

    return (
      (!totemFilters.address || normalizedAddress === totemFilters.address.toLowerCase()) &&
      (!totemFilters.store_name || normalizedStoreName === totemFilters.store_name.toLowerCase()) &&
      (!totemFilters.totem_serial || normalizedTotemSerial.includes(totemFilters.totem_serial.toLowerCase())) &&
      (!totemFilters.state || normalizedState === totemFilters.state.toLowerCase())
    );
  });

  // Gli espositori nascosti dai filtri si chiudono; con un solo espositore le mensole restano sempre aperte.
  // Lo stato cambia solo se serve, perche' filteredTotemsList e' un array nuovo a ogni render
  useEffect(() => {
    const visibleIds = filteredTotemsList.map((totem) => totem.id);
    const stillOpen = openTotemIds.filter((id) => visibleIds.includes(id));
    const nextOpen = isSingleTotem && visibleIds.length > 0 && stillOpen.length === 0 ? [visibleIds[0]] : stillOpen;

    if (nextOpen.length !== openTotemIds.length || nextOpen.some((id, idx) => id !== openTotemIds[idx])) {
      setOpenTotemIds(nextOpen);
    }
  }, [openTotemIds, filteredTotemsList, isSingleTotem]);

  return (
    <React.Fragment>
      {globalStats.length > 0 && (
        <div className="row d-flex justify-content-center flex-wrap m-auto">
          {globalStats.map((stats, index) => (
            <div
              key={index}
              className={`col-12 col-md-6 col-xxl-4 d-flex w-100 m-0 py-3 px-${![0, globalStats.length - 1].includes(index) ? '2' : '0'}`}
            >
              <LoopStatsChart stats={stats} index={index} showDetails={showDetails} onToggleDetails={toggleDetails} />
            </div>
          ))}
        </div>
      )}

      <div className="row mx-0 br-8px border-aiG-light-gray-imp bg-aiG-gray3-imp mb-3">
        <div className="col-12">
          <div className="row d-flex justify-content-between px-3 py-2">
            <div className="w-50">
              <div
                className="d-flex align-items-center justify-content-start text-left cursor-pointer"
                onClick={() => setFiltersEnabled(!filtersEnabled)}
              >
                <b>{I18n.t('js.filters.filters')}</b>
                <img
                  src={`/images/icons/black/arrow-${filtersEnabled ? 'up' : 'down'}.svg`}
                  className="cursor-pointer ml-2"
                  alt=""
                  width="10"
                  height="10"
                />
              </div>
            </div>
            <div className="w-50">
              <div
                className="d-flex align-items-center justify-content-end blue-label text-right cursor-pointer"
                onClick={() => {
                  setFilters(initialFilters);
                  setTotemFilters(initialTotemFilters);
                }}
              >
                <img src="/images/icons/blue/refresh.svg" className="mr-2" alt="" width="15" height="15" />
                {I18n.t('js.filters.cancelFilters')}
              </div>
            </div>
          </div>

          <div className={`row slide-animation-container slide-${filtersEnabled ? 'down' : 'up'}`}>
            <hr className="separator my-0 w-100" />
            <div className="col-12 pr-0">
              <div className="row w-100 py-2">
                {showTotemFilters && (
                  <React.Fragment>
                    <div className="col-6 col-md-4 col-lg-3 mb-2 py-1">
                      <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.address')}</p>
                      <select
                        value={totemFilters.address}
                        className={`form-control filter-input ${totemFilters.address ? 'selected-label' : 'border-none'}`}
                        onChange={(e) => onChangeTotemFilterHandler('address', e.target.value)}
                      >
                        <option value="">{I18n.t('js.loopDashboard.filters.allAddresses')}</option>
                        {addressOptions.map((address) => (
                          <option key={address} value={address}>{address}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 col-md-4 col-lg-3 mb-2 py-1">
                      <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.store')}</p>
                      <select
                        value={totemFilters.store_name}
                        className={`form-control filter-input ${totemFilters.store_name ? 'selected-label' : 'border-none'}`}
                        onChange={(e) => onChangeTotemFilterHandler('store_name', e.target.value)}
                      >
                        <option value="">{I18n.t('js.loopDashboard.filters.allStores')}</option>
                        {storeOptions.map((storeName) => (
                          <option key={storeName} value={storeName}>{storeName}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 col-md-4 col-lg-3 mb-2 py-1">
                      <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.totemSerial')}</p>
                      <input
                        type="text"
                        value={totemFilters.totem_serial}
                        className={`form-control filter-input ${totemFilters.totem_serial ? 'selected-label' : 'border-none'}`}
                        onChange={(e) => onChangeTotemFilterHandler('totem_serial', e.target.value)}
                      />
                    </div>
                    <div className="col-6 col-md-4 col-lg-3 mb-2 py-1">
                      <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.state')}</p>
                      <select
                        value={totemFilters.state}
                        className={`form-control filter-input ${totemFilters.state ? 'selected-label' : 'border-none'}`}
                        onChange={(e) => onChangeTotemFilterHandler('state', e.target.value)}
                      >
                        <option value="">{I18n.t('js.loopDashboard.filters.allStates')}</option>
                        {stateOptions.map((state) => (
                          <option key={state} value={state}>{expoTotemStateLabel(state)}</option>
                        ))}
                      </select>
                    </div>
                  </React.Fragment>
                )}
                <div className="col-6 col-md-4 col-lg-2 mb-2 py-1">
                  <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.cardName')}</p>
                  <input
                    type="text"
                    value={filters.name}
                    className={`form-control filter-input ${filters.name ? 'selected-label' : 'border-none'}`}
                    onChange={(e) => onChangeFilterHandler('name', e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-4 col-lg-2 mb-2 py-1">
                  <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.cardSerial')}</p>
                  <input
                    type="text"
                    value={filters.serial_number}
                    className={`form-control filter-input ${filters.serial_number ? 'selected-label' : 'border-none'}`}
                    onChange={(e) => onChangeFilterHandler('serial_number', e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-4 col-lg-2 mb-2 py-1">
                  <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.priceFrom')}</p>
                  <input
                    type="number"
                    min="0"
                    value={filters.price_from}
                    className={`form-control filter-input ${filters.price_from ? 'selected-label' : 'border-none'}`}
                    onChange={(e) => onChangeFilterHandler('price_from', e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-4 col-lg-2 mb-2 py-1">
                  <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.priceTo')}</p>
                  <input
                    type="number"
                    min="0"
                    value={filters.price_to}
                    className={`form-control filter-input ${filters.price_to ? 'selected-label' : 'border-none'}`}
                    onChange={(e) => onChangeFilterHandler('price_to', e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-4 col-lg-2 mb-2 py-1">
                  <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.gradeFrom')}</p>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.5"
                    value={filters.grade_from}
                    className={`form-control filter-input ${filters.grade_from ? 'selected-label' : 'border-none'}`}
                    onChange={(e) => onChangeFilterHandler('grade_from', e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-4 col-lg-2 mb-2 py-1">
                  <p className="mb-0 filter-label">{I18n.t('js.loopDashboard.filters.gradeTo')}</p>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.5"
                    value={filters.grade_to}
                    className={`form-control filter-input ${filters.grade_to ? 'selected-label' : 'border-none'}`}
                    onChange={(e) => onChangeFilterHandler('grade_to', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {filteredTotemsList.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center text-center" style={{ minHeight: '220px' }}>
          <span className="text-muted">
            <i className="fas fa-sad-tear fa-2x fa-align-middle" /> {I18n.t('js.loopDashboard.noTotemsFound')}
          </span>
        </div>
      ) : (
        <div className="loop-totems-grid">
          {filteredTotemsList.map(totem => {
            const isOpen = openTotemIds.includes(totem.id);
            const totemCells = totemCellsMap[totem.id];
            const isLoading = isOpen && loadingTotemIds.includes(totem.id) && !totemCells;

            return (
              <div key={totem.id} className="loop-totem-accordion">
                <ExpoTotemTile
                  totem={totem}
                  isDimmed={hasActiveSearch && !searchResults[totem.id]}
                  isSingleTotem={isSingleTotem}
                  isExpanded
                />

                {/* La dashboard dell'espositore resta sempre visibile, si aprono e chiudono solo le mensole */}
                <ExpoTotemAccordionStats
                  stats={totem.stats}
                  totemId={totem.id}
                  isOpen={isOpen}
                  onToggle={isSingleTotem ? undefined : () => handleTotemSelect(totem.id)}
                />

                <div className={`loop-totem-accordion__body${isOpen ? ' loop-totem-accordion__body--open' : ''}`}>
                  {isOpen && (
                    <div className="loop-totem-accordion__content">
                      {isLoading ? (
                        <div className="d-flex justify-content-center mt-5 mb-4">
                          <div className="d-flex align-items-center text-intense-blue text-weight-500">
                            <div className="spinner-border" role="status" />
                            <b className="ml-2">{I18n.t('js.common.loading')}</b>
                          </div>
                        </div>
                      ) : (
                        totemCells && (
                          <ExpoTotemGrid
                            totem={{ ...totem, ...totemCells }}
                            cellsSize={'large'}
                            highlightedCells={getHighlightedCells(totem.id)}
                            hasActiveSearch={hasActiveSearch}
                            onLabelClick={handleLabelClick}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {cellToEdit && (
        <LoopCellEditModal
          showModal={showCellModal}
          cell={cellToEdit}
          handleCloseModal={handleCloseCellModal}
          onUpdateSuccess={handleCellUpdateSuccess}
        />
      )}
      <ErrorModal
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
      />
    </React.Fragment>
  );
};

export default LoopDashboard;
